import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Users as UsersIcon, Shield, UserX, UserCheck, Trash2, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    title: '',
    role: 'staff'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/emr');
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await axios.post(
        `${API}/auth/users`,
        newUser,
        { withCredentials: true }
      );

      setSuccess(`User "${newUser.username}" created successfully!`);
      setNewUser({ username: '', password: '', full_name: '', title: '', role: 'staff' });
      setShowNewUserForm(false);
      fetchUsers();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      await axios.patch(
        `${API}/auth/users/${userId}/toggle-active`,
        {},
        { withCredentials: true }
      );

      setSuccess(`User ${action}d successfully`);
      fetchUsers();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/emr')}
                className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Dashboard</span>
              </button>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-medium">Admin Only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-yellow-400 mb-2">User Management</h1>
          <p className="text-gray-400">Manage employee accounts and access control</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Create New User Button */}
        <div className="mb-8">
          <button
            onClick={() => setShowNewUserForm(!showNewUserForm)}
            className="flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all"
          >
            <UserPlus className="w-5 h-5" />
            <span>{showNewUserForm ? 'Cancel' : 'Create New User'}</span>
          </button>
        </div>

        {/* New User Form */}
        {showNewUserForm && (
          <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-yellow-400 mb-6">Create New Employee Account</h2>
            <form onSubmit={handleCreateUser} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Username *</label>
                  <input
                    type="text"
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., jsmith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., John Smith"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Temporary Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      placeholder="Min 8 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-400"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={newUser.title}
                    onChange={(e) => setNewUser({ ...newUser, title: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    placeholder="e.g., RN, BSN.RN, LPN, NP"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Access Level *</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                >
                  <option value="staff">Staff</option>
                  <option value="admin">Admin</option>
                </select>
                <p className="text-xs text-gray-400 mt-2">Staff can manage patients and visits. Admin has full system access.</p>
              </div>

              <button
                type="submit"
                className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-8 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-700 transition-all"
              >
                Create User Account
              </button>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-yellow-500/20">
            <h2 className="text-2xl font-bold text-yellow-400 flex items-center">
              <UsersIcon className="w-6 h-6 mr-2" />
              Employee Accounts ({users.length})
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading users...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No users found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Username</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Full Name</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Access Level</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-800/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium">{u.username}</span>
                          {u.id === user.id && (
                            <span className="text-xs bg-yellow-400/20 text-yellow-400 px-2 py-1 rounded">You</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-300">{u.full_name}</td>
                      <td className="px-6 py-4">
                        <span className="text-yellow-400 font-medium">{u.title || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                          u.role === 'admin' ? 'bg-yellow-400/20 text-yellow-400' : 'bg-gray-700 text-gray-300'
                        }`}>
                          {u.role === 'admin' && <Shield className="w-3 h-3" />}
                          <span className="capitalize">{u.role}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                          u.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {u.is_active ? (
                            <><UserCheck className="w-3 h-3" /> <span>Active</span></>
                          ) : (
                            <><UserX className="w-3 h-3" /> <span>Inactive</span></>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {u.id !== user.id && (
                          <button
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                              u.is_active
                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;